import React, {useCallback, useEffect, useRef} from 'react';
import {useHighlighterContext} from 'Contexts/HighlighterContext';
import {AstView, EnrichedAstNode} from 'rendition-core/types';
import {useAddKeyFramesToDocument} from 'Hooks/useAddKeyFramesToDocument';
import {DragSelector} from '../Builder/DragSelector';
import {useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import {usePreviewContext} from 'Contexts/PreviewContext';
import _ from 'lodash';
import {CanvasTopMenu} from 'Components/CanvasTopMenu';
import {useViewStore} from 'stores/view';
import {useKeyState} from 'Hooks/useKeyState';
import {useKeyBind} from 'Hooks/useKeyBind';

interface Props {
    background: string;
    styles?: React.CSSProperties;
    resizable?: boolean;
    view: AstView;
    rootNode?: EnrichedAstNode;
    overLayedChildren?: React.ReactNode;
    rendersSelectedNodeAsRoot?: boolean;
    onNodeSelection?: (nodeIds: string[]) => void;
    hideTopMenu?: boolean;
    canvasInitialOffsetX?: number;
}
let target = {x: 0, y: 0};

export const ScalablePreview: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    overLayedChildren,
    resizable = true,
    view,
    background,
    styles,
    rootNode = view.tree,
    rendersSelectedNodeAsRoot = false,
    hideTopMenu = false,
    onNodeSelection,
    canvasInitialOffsetX = 0,
}) => {
    const canvasRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const transformRef = useRef({scale: 1, x: 0, y: 0});

    const highLightContext = useHighlighterContext();
    const {scaleRef} = useNodeCallbackContext();

    const startingViewportSizeInSlider: number = view.tree.position.width;

    const previewSize = useViewStore.use.previewWidth();
    const setPreviewSize = useViewStore.use.setPreviewWidth();

    const {onNodeClick, onNodeMouseEnter, onNodeMouseLeave} = usePreviewContext();

    const applySizing = useCallback(() => {
        if (canvasRef.current) {
            canvasRef.current.style.transform = `translate(${transformRef.current.x}px, ${transformRef.current.y}px) scale(${transformRef.current.scale})`;
        }
        scaleRef.current = transformRef.current.scale;
    }, [scaleRef]);

    const [cursor, setCursor] = React.useState<
        'cursor-auto' | 'cursor-grab' | 'cursor-grabbing' | 'cursor-zoom-in' | 'cursor-zoom-out'
    >('cursor-auto');
    const isKeySpacePressed = useKeyState(' ');
    const isKeyZPressed = useKeyState('z');

    // Zoom in to center.
    useKeyBind('=', (e) => {
        zoomIn('center', -50);
        e.preventDefault();
    });
    useKeyBind('meta+=', (e) => {
        // Prevent browser from zooming in.
        zoomIn('center', -50);
        e.preventDefault();
    });

    // Zoom out to center.
    useKeyBind('-', (e) => {
        zoomIn('center', 33);
        e.preventDefault();
    });
    useKeyBind('meta+-', (e) => {
        zoomIn('center', 33);
        e.preventDefault();
    });

    const isKeyZAltPressed = useKeyState('x'); // temporary until we can figure out how to get alt+z working.
    // const isKeyZAltPressed = useKeyState('Ω', 'altKey'); // z + alt = Ω, for Bernardo, at least.

    useEffect(() => {
        setPreviewSize(startingViewportSizeInSlider);

        const rootWidth = rootRef.current?.clientWidth || 1000;
        const rootHeight = rootRef.current?.clientHeight || 1000;

        const canvasWidth = canvasRef.current?.clientWidth || 1000;

        const scaleHeight = (rootHeight - 200) / view.tree.position.height;
        const scaleWidth = (rootWidth - 600) / view.tree.position.width;
        const scale = Math.min(scaleHeight, scaleWidth);
        transformRef.current.scale = scale;

        transformRef.current.x = rootWidth / 2 - (canvasWidth * scale) / 2 + canvasInitialOffsetX;
        transformRef.current.y = 100;

        applySizing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const rootAdjustedPreviewSize = rendersSelectedNodeAsRoot
            ? previewSize * (view.tree.position.width / rootNode.position.width)
            : previewSize;

        document.documentElement.style.setProperty('--preview-width', `${rootAdjustedPreviewSize}px`);
    }, [previewSize, rendersSelectedNodeAsRoot, rootNode.position.width, view.tree.position.width]);

    useAddKeyFramesToDocument(view);

    const zoomIn = (mouse: {x: number; y: number} | 'center', deltaY: number) => {
        if (!rootRef.current) {
            return;
        }
        const rec = rootRef.current.getBoundingClientRect();

        const mouseX = mouse === 'center' ? rec.width / 2 : mouse.x - rec.left;
        const mouseY = mouse === 'center' ? rec.height / 2 : mouse.y - rec.top;

        target.x = (mouseX - transformRef.current.x) / transformRef.current.scale;
        target.y = (mouseY - transformRef.current.y) / transformRef.current.scale;

        transformRef.current.scale += -1 * deltaY * 0.01 * transformRef.current.scale;
        transformRef.current.scale = _.clamp(transformRef.current.scale, 0.1, 10);

        transformRef.current.x = -target.x * transformRef.current.scale + mouseX;
        transformRef.current.y = -target.y * transformRef.current.scale + mouseY;
        scaleRef.current = transformRef.current.scale;
        applySizing();
    };

    useEffect(() => {
        if (!rootRef.current || !canvasRef.current) {
            return;
        }

        const onWheel = (e: WheelEvent) => {
            if (!rootRef.current) {
                return;
            }
            // https://stackoverflow.com/a/60234045/6923589
            if (e.ctrlKey) {
                // Zoom out is 1.5x faster than zoom in, so we must adjust for that.
                const zoomFactor = 2 * (e.deltaY > 0 ? 1.5 : 1);
                zoomIn({x: e.pageX, y: e.pageY}, e.deltaY / zoomFactor);
            } else {
                transformRef.current.y += e.deltaY * -1;
                transformRef.current.x += e.deltaX * -1;
                applySizing();
            }
            e.preventDefault();
        };

        const globalPinchZoomPrevent = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        rootRef.current.addEventListener('wheel', onWheel, {passive: false});
        window.addEventListener('wheel', globalPinchZoomPrevent, {passive: false});

        const refToRefRoot = rootRef.current;
        return () => {
            refToRefRoot.removeEventListener('wheel', onWheel, false);
            window.addEventListener('wheel', globalPinchZoomPrevent, {passive: false});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootRef, highLightContext, scaleRef]);

    useEffect(() => {
        if (!rootRef.current || !canvasRef.current) {
            return;
        }

        let isDragging = false;
        let lastMousePosition: {x: number; y: number} | null = null;

        const onMouseDown = (e: MouseEvent) => {
            if (e.buttons === 1) {
                lastMousePosition = {x: e.clientX, y: e.clientY};
                if (isKeyZAltPressed === true) {
                    // TODO understand why zoom out is more agressive than zoom in.
                    return zoomIn({x: e.pageX, y: e.pageY}, 33);
                } else if (isKeyZPressed === true) {
                    return zoomIn({x: e.pageX, y: e.pageY}, -50);
                } else if (isKeySpacePressed === true) {
                    isDragging = true;
                    setCursor('cursor-grabbing');
                }
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            if (isDragging && isKeySpacePressed === true) {
                // Scale can go from 0 to 10.
                const scale = 1;
                const dragX = (e.clientX - lastMousePosition!.x) * scale;
                const dragY = (e.clientY - lastMousePosition!.y) * scale;

                transformRef.current.x += dragX;
                transformRef.current.y += dragY;

                applySizing();
                lastMousePosition = {x: e.clientX, y: e.clientY};
            }
        };

        const onMouseUp = (e: MouseEvent) => {
            isDragging = false;
            if (isKeySpacePressed === true) {
                setCursor('cursor-grab');
            } else {
                setCursor('cursor-auto');
            }
            lastMousePosition = null;
        };

        rootRef.current.addEventListener('mousedown', onMouseDown);
        rootRef.current.addEventListener('mousemove', onMouseMove);
        rootRef.current.addEventListener('mouseup', onMouseUp);

        const refToRefRoot = rootRef.current;
        return () => {
            refToRefRoot.removeEventListener('mousedown', onMouseDown);
            refToRefRoot.removeEventListener('mousemove', onMouseMove);
            refToRefRoot.removeEventListener('mouseup', onMouseUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootRef, canvasRef, isKeySpacePressed, isKeyZPressed, isKeyZAltPressed]);

    // Update cursor accordingly when key is pressed
    useEffect(() => {
        if (isKeyZAltPressed) {
            return setCursor('cursor-zoom-out');
        } else if (isKeyZPressed) {
            return setCursor('cursor-zoom-in');
        } else if (cursor === 'cursor-auto' && isKeySpacePressed) {
            return setCursor('cursor-grab');
        } else if (
            (cursor === 'cursor-zoom-out' && !isKeyZAltPressed) ||
            (cursor === 'cursor-zoom-in' && !isKeyZPressed) ||
            (cursor === 'cursor-grab' && !isKeySpacePressed)
        ) {
            return setCursor('cursor-auto');
        }
    }, [cursor, isKeyZAltPressed, isKeySpacePressed, isKeyZPressed]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    width: '100vw',
                    height: 'calc(100vh)',
                    overflow: 'hidden',
                }}
                ref={rootRef}
                onMouseDown={onNodeClick}
                onMouseEnter={onNodeMouseEnter}
                onMouseLeave={onNodeMouseLeave}
            >
                {!hideTopMenu && <CanvasTopMenu resizable={resizable} selectedRootNode={rootNode} />}
                <DragSelector isSpaceKeyPressed={isKeySpacePressed} view={view} onNodeSelection={onNodeSelection}>
                    <div
                        ref={canvasRef}
                        style={{
                            transformOrigin: '0 0',
                            willChange: 'transform',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: previewSize,
                                    overflow: 'hidden',
                                    transformOrigin: 'top center',
                                    position: 'relative',
                                    backgroundColor: background,
                                    transition: 'width 100ms',
                                    ...styles,
                                }}
                            >
                                {children}
                            </div>
                        </div>
                    </div>
                </DragSelector>
                {cursor !== 'cursor-auto' && (
                    <div className={`${cursor} absolute bottom-0 right-0 top-0 h-full w-full opacity-0`}></div>
                )}
            </div>
            {overLayedChildren}
        </div>
    );
};

// const STEP = 40;

// const useSmoothNumber = (targetNumber: number) => {
//     const [currentNumber, setCurrentNumber] = useState<number>(targetNumber);

//     useEffect(() => {
//         const updateNumber = () => {
//             if (Math.abs(targetNumber - currentNumber) > STEP) {
//                 setCurrentNumber((prevNumber) => {
//                     const step = Math.max(STEP, Math.abs(targetNumber - prevNumber) / 4); // 4 is a magic number -Copilot 2023
//                     const increment = targetNumber > prevNumber ? step : -step;
//                     return prevNumber + increment;
//                 });
//             } else {
//                 setCurrentNumber(targetNumber);
//             }
//         };

//         if (currentNumber !== targetNumber) {
//             const timeoutId = setTimeout(updateNumber, 10);
//             return () => clearTimeout(timeoutId);
//
